import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import BankAccountBlacklist from "../entities/bankAccountBlacklist";
import RefundApprovedResponse from "entities/refundApprovedResponse";
import type EventBus from "../services/eventBus";
import { serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import type { ICreateRepository } from "./types";

export default class BankAccountBlacklistRepository
  extends RepositoryBase<BackOfficeConnector>
  implements ICreateRepository<BankAccountBlacklist>
{
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(pagination: IPagination) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("refunds")
          .all("blacklisted_bank_accounts")
          .getPagedEntities<BankAccountBlacklist>(BankAccountBlacklist, pagination),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  get(bankAccountBlacklistId: string) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("refunds")
          .one("blacklisted_bank_accounts", bankAccountBlacklistId)
          .getEntity(BankAccountBlacklist),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  create(bankAccountBlacklist: BankAccountBlacklist) {
    return this.callApi(
      api =>
        api.all("finance").all("refunds").all("blacklisted_bank_accounts").postEntity(bankAccountBlacklist, BankAccountBlacklist),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  update(bankAccountBlacklist: BankAccountBlacklist) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("refunds")
          .one("blacklisted_bank_accounts", bankAccountBlacklist.id)
          .putEntity(bankAccountBlacklist, BankAccountBlacklist),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  delete(bankAccountBlacklist: BankAccountBlacklist) {
    return this.callApi(api =>
      api.all("finance").all("refunds").one("blacklisted_bank_accounts", bankAccountBlacklist.id).delete({})
    );
  }

  getAllApprovedRefunds(bankAccountBlacklist: BankAccountBlacklist) {
    const { bankAccount } = bankAccountBlacklist;
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("approved_refund_search")
          .postEntities({ accountNumber: bankAccount.accountNumber, bank: bankAccount.bank }, RefundApprovedResponse),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  disapproveAllRefunds(bankAccountBlacklist: BankAccountBlacklist) {
    const { bankAccount } = bankAccountBlacklist;

    return this.callApi(
      api =>
        api
          .all("finance")
          .all("refunds")
          .all("approvals")
          .delete({}, serializeFilters({ accountNumber: bankAccount.accountNumber, bank: bankAccount.bank }, true)),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }
}
